import { Component, Input, TemplateRef, ContentChild, ElementRef, ViewChild, AfterViewInit, OnDestroy, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Component({
  selector: 'app-tooltip',
  imports: [CommonModule],
  templateUrl: './tooltip.component.html',
  styles: []
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  @Input() text: string = '';
  @Input() position: TooltipPosition = 'top';
  @Input() delay: number = 200;
  @Input() disabled: boolean = false;
  
  @ViewChild('tooltipTrigger', { static: false }) triggerElement!: ElementRef<HTMLElement>;
  @ViewChild('tooltipContent', { static: false }) tooltipContent!: ElementRef<HTMLElement>;
  
  @ContentChild('tooltipTemplate') templateRef?: TemplateRef<any>;

  showTooltip = signal(false);
  tooltipStyles = signal<{ [key: string]: string }>({});

  private showTimeout?: number;
  private hideTimeout?: number;
  private mutationObserver?: MutationObserver;

  ngAfterViewInit(): void {
    if (this.triggerElement) {
      this.setupEventListeners();
      this.observeDarkMode();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private setupEventListeners(): void {
    const element = this.triggerElement.nativeElement;
    
    element.addEventListener('mouseenter', () => this.onMouseEnter());
    element.addEventListener('mouseleave', () => this.onMouseLeave());
    element.addEventListener('focus', () => this.onFocus());
    element.addEventListener('blur', () => this.onBlur());
  }

  private observeDarkMode(): void {
    // Observe dark mode class changes on document
    this.mutationObserver = new MutationObserver(() => {
      this.updateTooltipPosition();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private onMouseEnter(): void {
    if (this.disabled) return;
    
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }

    this.showTimeout = window.setTimeout(() => {
      this.showTooltip.set(true);
      setTimeout(() => this.updateTooltipPosition(), 10);
    }, this.delay);
  }

  private onMouseLeave(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = undefined;
    }

    this.hideTimeout = window.setTimeout(() => {
      this.showTooltip.set(false);
    }, 100);
  }

  private onFocus(): void {
    if (this.disabled) return;
    this.showTooltip.set(true);
    setTimeout(() => this.updateTooltipPosition(), 10);
  }

  private onBlur(): void {
    this.showTooltip.set(false);
  }

  private updateTooltipPosition(): void {
    if (!this.showTooltip() || !this.triggerElement || !this.tooltipContent) return;

    // Get the first child element (the actual trigger content)
    const triggerContent = this.triggerElement.nativeElement.firstElementChild as HTMLElement;
    if (!triggerContent) return;

    const trigger = triggerContent.getBoundingClientRect();
    const gap = 12; // Gap between trigger and tooltip (increased for better spacing)
    const styles: { [key: string]: string } = {};

    // Calculate initial position - tooltip appears right next to the element
    switch (this.position) {
      case 'top':
        styles['bottom'] = `${window.innerHeight - trigger.top + gap}px`;
        styles['left'] = `${trigger.left + trigger.width / 2}px`;
        styles['transform'] = 'translateX(-50%)';
        break;
      case 'bottom':
        styles['top'] = `${trigger.bottom + gap}px`;
        styles['left'] = `${trigger.left + trigger.width / 2}px`;
        styles['transform'] = 'translateX(-50%)';
        break;
      case 'left':
        styles['right'] = `${window.innerWidth - trigger.left + gap}px`;
        styles['top'] = `${trigger.top + trigger.height / 2}px`;
        styles['transform'] = 'translateY(-50%)';
        break;
      case 'right':
        styles['left'] = `${trigger.right + gap}px`;
        styles['top'] = `${trigger.top + (trigger.height / 2)}px`;
        styles['transform'] = 'translateY(-50%)';
        break;
    }

    this.tooltipStyles.set(styles);

    // Adjust position after tooltip renders to prevent viewport overflow
    requestAnimationFrame(() => {
      if (!this.tooltipContent) return;
      const tooltipRect = this.tooltipContent.nativeElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const adjustedStyles = { ...styles };

      // Adjust horizontal position if tooltip overflows (for right position)
      if (this.position === 'right') {
        if (tooltipRect.right > viewportWidth - gap) {
          // If tooltip goes off right edge, position it to the left instead
          adjustedStyles['right'] = `${window.innerWidth - trigger.left + gap}px`;
          adjustedStyles['left'] = 'auto';
          adjustedStyles['transform'] = 'translateY(-50%)';
        }
      } else if (this.position === 'left') {
        if (tooltipRect.left < gap) {
          // If tooltip goes off left edge, position it to the right instead
          adjustedStyles['left'] = `${trigger.right + gap}px`;
          adjustedStyles['right'] = 'auto';
          adjustedStyles['transform'] = 'translateY(-50%)';
        }
      } else {
        // For top/bottom positions, adjust horizontal overflow
        if (tooltipRect.left < gap) {
          adjustedStyles['left'] = `${trigger.left + gap}px`;
          adjustedStyles['transform'] = 'translateX(0)';
        } else if (tooltipRect.right > viewportWidth - gap) {
          adjustedStyles['left'] = `${trigger.right - gap}px`;
          adjustedStyles['transform'] = 'translateX(-100%)';
        }
      }

      // Adjust vertical position if tooltip overflows
      if (tooltipRect.top < gap && this.position === 'top') {
        adjustedStyles['top'] = `${trigger.bottom + gap}px`;
        adjustedStyles['bottom'] = 'auto';
        adjustedStyles['transform'] = 'translateX(-50%)';
      } else if (tooltipRect.bottom > viewportHeight - gap && this.position === 'bottom') {
        adjustedStyles['bottom'] = `${window.innerHeight - trigger.top + gap}px`;
        adjustedStyles['top'] = 'auto';
        adjustedStyles['transform'] = 'translateX(-50%)';
      }

      this.tooltipStyles.set(adjustedStyles);
    });
  }

  private cleanup(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  get tooltipClasses(): string {
    const baseClasses = [
      'px-3',
      'py-2',
      'text-sm',
      'font-medium',
      'rounded-lg',
      'shadow-lg',
      'pointer-events-none',
      'transition-opacity',
      'duration-200',
      'whitespace-nowrap',
      'bg-white',
      'dark:bg-black',
      'text-black',
      'dark:text-white',
      'border',
      'border-gray-200',
      'dark:border-gray-700'
    ];

    if (!this.showTooltip()) {
      baseClasses.push('opacity-0', 'invisible');
    } else {
      baseClasses.push('opacity-100', 'visible');
    }

    return baseClasses.join(' ');
  }
}

